/**
 * The one data-fetching hook these screens need. No cache, no library — the
 * admin panel has a single user and a handful of routes, and a stale-while-
 * revalidate layer would be more machinery than the problem deserves.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export interface AsyncState<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  /** Refetch, e.g. after a mutation. */
  reload: () => void;
  /** Replace the data locally after a mutation that already returned it. */
  set: (next: T) => void;
}

export function useAsync<T>(
  fn: () => Promise<T>,
  deps: unknown[],
  options: { enabled?: boolean } = {},
): AsyncState<T> {
  const enabled = options.enabled ?? true;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(enabled);
  const [nonce, setNonce] = useState(0);

  // Guards against a slow first request overwriting a newer one.
  const generation = useRef(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const current = ++generation.current;
    setLoading(true);
    setError(null);

    fnRef
      .current()
      .then((result) => {
        if (generation.current !== current) return;
        setData(result);
      })
      .catch((err: unknown) => {
        if (generation.current !== current) return;
        setError(err);
      })
      .finally(() => {
        if (generation.current !== current) return;
        setLoading(false);
      });

    return () => {
      // Mark in-flight results stale rather than aborting: the request is
      // cheap and its response may still populate a shared cache later.
      generation.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, error, loading, reload, set: setData };
}
